<template>
  <div>
    <!-- Add a rebate form -->
    <template v-if="isAddRebate">
      <RebateForm
        :rebateTierInfo="rebateTierInfo"
        :rebateApplicants="rebateApplicants"
        :selectedApplicant="selectedApplicant"
        :coverComposition="currentComposition"
        :rebateFormData="formResponse"
        :myCircumstances="getMyCircumstances"
        :rebateTierLevel="formResponse.rebateTier"
        :helperText="helperText"
        :isChangeCover="true"
        @rebateFormUpdated="getRebateForm"
        ref="rebateForm"
      >
        <template v-slot:pageTitle>
          <h3 class="h6 sub-heading-underlined">{{ referenceData.additionalInformationRebateHeading.informationText }}</h3>
        </template>
        <template v-slot:commencesDate>
          <FormField :required="false" labelText="Date that the rebate commences" class="no-margin-bottom">
            <p>{{ format(new Date(), 'dd MMMM yyyy') }}</p>
          </FormField>
        </template>
      </RebateForm>
    </template>

    <!-- Update a rebate tier -->
    <template v-else-if="isUpdateRebateTier">
      <SectionSeparatorWrapper headerText="Rebate" headerRequiredFieldsText="indicates required field" :separator="false">
        <h3 class="h6 sub-heading-underlined">
          {{ referenceData.additionalInformationUpdateRebateHeading.informationText }}
        </h3>
        <section>
          <FormField labelText="Rebate applicant" :required="true" :helperText="helperText">
            <p class="disabled-input">{{ mapApplicantName(getRebateApplicant()) }}</p>
          </FormField>
          <FormField labelText="Rebate registration date" :required="false">
            <p>{{ currentDate }}</p>
          </FormField>
          <FormField labelText="Rebate reduction commencement date" :required="false">
            <p>{{ currentDate }}</p>
          </FormField>
          <!-- Circumstances -->
          <FormField labelText="My circumstances" :required="true">
            <div class="input-grid">
              <ul class="radio-group large">
                <li v-for="item in referenceData.additionalInformationCircumstanceTable" :key="item.returnValue">
                  <input
                    type="radio"
                    disabled
                    v-model="v$.formResponse.myCircumstances.$model"
                    :id="item.returnValue"
                    :value="item.returnValue"
                    name="currentMyCircumstance"
                    :error="v$.formResponse.myCircumstances.$error"
                    errorMessage="Please include a response for your circumstances."
                  />
                  <label :for="item.returnValue">{{ item.display }}</label>
                </li>
              </ul>
            </div>
          </FormField>
          <!-- Household Income -->
          <FormField
            labelText="My household income for the purposes of determining rebate is"
            forId="rebateTier"
            :required="true"
            :helperText="referenceData.additionalInformationRebateDetails.informationText"
          >
            <DropdownSelect :disabled="true" :items="getRebateTierList(formResponse.myCircumstances)" v-model="v$.formResponse.rebateTier.$model" />
          </FormField>
        </section>
      </SectionSeparatorWrapper>
    </template>

    <!-- Withdraw a rebate -->
    <template v-else-if="isWithdrawRebateTier">
      <SectionSeparatorWrapper headerText="Rebate" headerRequiredFieldsText="indicates required field" :separator="false">
        <h3 class="h6 sub-heading-underlined">{{ referenceData.additionalInformationWithdrawRebateHeading.informationText }}</h3>
        <section>
          <FormField labelText="Effective withdrawal date" :required="true" :helperText="helperText">
            <p>{{ currentDate }}</p>
          </FormField>
          <FormField labelText="Rebate reduction commencement date" :required="true" v-if="rebateCommencementDate">
            <p>{{ format(new Date(rebateCommencementDate), 'dd / MM / yyyy') }}</p>
          </FormField>
          <FormField labelText="My circumstances" :required="true">
            <div class="input-grid">
              <ul class="radio-group large">
                <li v-for="item in referenceData.additionalInformationCircumstanceTable" :key="item.returnValue">
                  <input
                    type="radio"
                    disabled
                    v-model="v$.formResponse.myCircumstances.$model"
                    :id="item.returnValue"
                    :value="item.returnValue"
                    name="currentMyCircumstance"
                    :error="v$.formResponse.myCircumstances.$error"
                    errorMessage="Please include a response for your circumstances."
                  />
                  <label :for="item.returnValue">{{ item.display }}</label>
                </li>
              </ul>
            </div>
          </FormField>
          <FormField labelText="Rebate applicant" :required="true">
            <p class="disabled-input">{{ mapApplicantName(getRebateApplicant()) }}</p>
          </FormField>
          <FormField labelText="Rebate tier" :required="true" v-if="getMyCircumstances || currentRebateTier">
            <p>{{ getRebateTierLabel(getMyCircumstances, currentRebateTier) }}</p>
          </FormField>
        </section>
      </SectionSeparatorWrapper>
    </template>

    <!-- Rebate add/update/withdraw not allowed because of rebate registration status & event type rules, hence show below message -->
    <template v-else>
      <FormField>
        <Alert alertType="info" class="inline-message" :message="'You cannot currently update your rebate. Please contact Defence Health.'" />
      </FormField>
    </template>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { required } from '@vuelidate/validators';
import { RebateForm, SectionSeparatorWrapper, FormField, Alert } from '@/components';
import { NonDependantFlexCodes, OperationType, RebateTierDisplayWithoutIncomeBrackets } from '@/enums';
import { MyCircumstances, RebateTier, CoverComposition, RebateEventType, RebateEventStatus, RelationshipValue } from '@general/js/enums';
import { reverseFormatFormDate, formatFormDate, formatMedicareCardNumber, removeWhitespaceFromString } from '@general/js/utils';
import { DropdownSelect } from '@general/js/components/index';
import { mapMedicareCardCode, isEmptyString } from '@/utils';
import CaptureInformationMixins from '../CaptureInformationMixins';
import ChangeCoverMixins from '../ChangeCoverMixins';
import useVuelidate from '@vuelidate/core';

export default {
  name: 'Rebate',

  setup() {
    return { v$: useVuelidate() };
  },

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    Alert,
    DropdownSelect,
    FormField,
    RebateForm,
    SectionSeparatorWrapper,
  },

  props: {
    informationReferenceData: {
      type: Object,
      required: true,
    },
  },

  beforeMount() {
    this.prefillData();
  },

  data() {
    return {
      currentDate: format(new Date(), 'dd / MM / yyyy'),
      selectedApplicant: null,
      isFormValid: false,
      formResponse: {
        isRebateApplied: null,
        isPolicyCovered: null,
        isMedicareEligible: null,
        medicareCard: null,
        myCircumstances: null,
        rebateApplicant: null,
        rebateTier: null,
        rebateDeclaration: false,
        rebateAcceptance: false,
      },
      rebateCommencementDate: null,
      formPrefilled: false,
    };
  },

  validations() {
    if (this.isUpdateRebateTier || this.isWithdrawRebateTier) {
      return {
        formResponse: {
          myCircumstances: { required },
          rebateTier: { required },
        },
      };
    }
    return { formResponse: {} };
  },

  watch: {
    formResponse: {
      deep: true,
      handler() {
        if (this.isUpdateRebateTier || this.isWithdrawRebateTier) {
          this.isFormValid = !this.v$.formResponse.$invalid;
        } else if (this.formResponse.isRebateApplied === false) {
          this.isFormValid = true;
        } else if (!this.isAddRebate && !this.isUpdateRebateTier && !this.isWithdrawRebateTier) {
          // pass validation if not allowed to add/update/withdraw rebate due to registration status & event type rules
          this.isFormValid = true;
        }
        const data = {
          isInvalid: !this.isFormValid,
        };
        if (this.isFormValid) {
          data.response = this.generatePayload();
        }
        this.$emit('formUpdated', data);
      },
    },
  },

  computed: {
    rebateTierInfo() {
      const incomeTierSingleOptions = this.informationReferenceData.incomeTierSingleOptions.map((op) => {
        return {
          dhl_code: op.returnValue,
          dhl_name: op.display,
        };
      });
      const incomeTierFamilyOptions = this.informationReferenceData.incomeTierFamilyOptions.map((op) => {
        return {
          dhl_code: op.returnValue,
          dhl_name: op.display,
        };
      });
      return [
        {
          myCircumstance: MyCircumstances.SINGLE,
          rebateTierOptions: incomeTierSingleOptions,
        },
        {
          myCircumstance: MyCircumstances.OTHER,
          rebateTierOptions: incomeTierFamilyOptions,
        },
      ];
    },

    rebateApplicants() {
      const applicantsList = [];
      this.memberDetails.forEach((member) => {
        applicantsList.push({
          memberNumber: this.memberNumber,
          personCode: member.personCode,
          firstName: member.firstName,
          lastName: member.lastName,
          relationship: member.relationship.name,
        });
      });

      const nonDependantList = Object.keys(NonDependantFlexCodes).map((str) => {
        return str.replace(/_/i, ' ');
      });

      const policyHolder = applicantsList.find((obj) => {
        return obj.relationship.toUpperCase() === RelationshipValue.POLICY_HOLDER.toUpperCase();
      });

      const nonDependants = applicantsList.filter((obj) => {
        if (obj.relationship.toUpperCase() === RelationshipValue.POLICY_HOLDER.toUpperCase()) {
          return false;
        }
        return nonDependantList.includes(obj.relationship.toUpperCase());
      });

      // Partner step
      if (this.newCompositionHavePartner) {
        if (this.showNewPartnerSection) {
          const newPartner = {
            firstName: this.partnerStep.newPartner.firstName,
            lastName: this.partnerStep.newPartner.lastName,
            relationship: 'Spouse',
            personCode: this.partnerStep.newPartner.chosenDuplicatePerson?.personCode ?? null,
          };
          return [policyHolder, newPartner];
        }
        return [policyHolder, ...nonDependants];
      }

      return [policyHolder];
    },

    rebateRegistrationEventStatus() {
      return this.membershipPolicy.rebateRegistrationEventStatus;
    },

    rebateRegistrationEventType() {
      return this.membershipPolicy.rebateRegistrationEventType;
    },

    isAddRebate() {
      const regEventType = Object.keys(RebateEventType).find((key) => {
        return RebateEventType[key] === RebateEventType.R;
      }); // 'R'

      return (
        (this.currentRebateTier === RebateTier.NONE || isEmptyString(this.currentRebateTier)) &&
        // ensure the [Rebate Event Type is not R] OR  [Rebate Event Type is R AND Rebate Registration Status is REJECTED]
        (this.rebateRegistrationEventType !== regEventType ||
          (this.rebateRegistrationEventType === regEventType && this.rebateRegistrationEventStatus.toLowerCase() === RebateEventStatus.REJECTED.toLowerCase()))
      );
    },

    isUpdateRebateTier() {
      const regEventType = Object.keys(RebateEventType).find((key) => {
        return RebateEventType[key] === RebateEventType.R;
      }); // 'R'

      return (
        this.changeCoverData.step2.rebateTier?.toUpperCase() !== RebateTier.NONE.toUpperCase() &&
        !isEmptyString(this.currentRebateTier) &&
        this.currentRebateTier?.toUpperCase() !== RebateTier.NONE.toUpperCase() &&
        // ensure the Rebate Event Type is R Rebate Registration Status is REGISTRATION ACCEPTED
        this.rebateRegistrationEventType === regEventType &&
        this.rebateRegistrationEventStatus.toLowerCase() === RebateEventStatus.REGISTRATION_ACCEPTED.toLowerCase()
      );
    },

    isWithdrawRebateTier() {
      const regEventType = Object.keys(RebateEventType).find((key) => {
        return RebateEventType[key] === RebateEventType.R;
      }); // 'R'

      const withdrawEventType = Object.keys(RebateEventType).find((key) => {
        return RebateEventType[key] === RebateEventType.W;
      }); // 'W'

      return (
        this.currentRebateTier?.toUpperCase() !== RebateTier.NONE.toUpperCase() &&
        this.currentRebateTier !== null &&
        this.changeCoverData.step2.rebateTier?.toUpperCase() === RebateTier.NONE.toUpperCase() &&
        // ensure the [Rebate Event Type is R AND Rebate Registration Status is REGISTRATION ACCEPTED] OR  [Rebate Event Type is W AND Rebate Registration Status is REJECTED]
        ((this.rebateRegistrationEventType === regEventType && this.rebateRegistrationEventStatus.toLowerCase() === RebateEventStatus.REGISTRATION_ACCEPTED.toLowerCase()) ||
          (this.rebateRegistrationEventType === withdrawEventType && this.rebateRegistrationEventStatus.toLowerCase() === RebateEventStatus.REJECTED.toLowerCase()))
      );
    },

    noRebateUpdates() {
      if (this.isUpdateRebateTier) {
        return this.currentRebateTier === this.formResponse.rebateTier;
      }
      return false;
    },

    helperText() {
      if (this.isAddRebate) {
        return this.referenceData.additionalInformationRebateSupportText.informationText;
      }
      if (this.isUpdateRebateTier) {
        return this.referenceData.additionalInformationUpdateRebateSupportText.informationText;
      }
      return this.referenceData.additionalInformationWithdrawRebateSupportText.informationText;
    },

    getMyCircumstances() {
      const composition = this.changeCoverData.step2.composition;
      if (composition === CoverComposition.SINGLE) {
        return this.changeCoverData.step2.myCircumstances;
      }
      return MyCircumstances.OTHER;
    },
  },

  methods: {
    getRebateForm(res) {
      this.formResponse = { ...res?.data };
      this.isFormValid = res?.isFormValid ?? false;
    },

    getRebateTierList(myCircumstances) {
      let rebateTierList;
      if (myCircumstances === MyCircumstances.SINGLE) {
        rebateTierList = this.informationReferenceData.incomeTierSingleOptions;
      } else {
        rebateTierList = this.informationReferenceData.incomeTierFamilyOptions;
      }

      return rebateTierList
        .filter((op) => {
          return op.returnValue !== RebateTier.NONE;
        })
        .map((op) => {
          return {
            value: op.returnValue,
            name: `${RebateTierDisplayWithoutIncomeBrackets[op.returnValue]} - ${op.display}`,
          };
        });
    },

    // Map rebate applicant personCode to name
    mapApplicantName(personCode) {
      const currentApplicant = this.rebateApplicants.find((person) => {
        return person.personCode === personCode;
      });
      if (currentApplicant) {
        return `${currentApplicant.firstName} ${currentApplicant.lastName}`;
      }
      return '';
    },

    // Format Rebate Tier value, e.g, from "BASE_TIER" to "Base Tier"
    formatRebateTierValue(rebateTier) {
      return RebateTierDisplayWithoutIncomeBrackets[rebateTier.toUpperCase()];
    },

    getRebateTierLabel(myCircumstances, rebateTier) {
      let myCircumstancesValue = myCircumstances;
      if (myCircumstancesValue == null) {
        myCircumstancesValue = this.currentComposition !== CoverComposition.SINGLE ? MyCircumstances.OTHER : MyCircumstances.SINGLE;
      }
      if (rebateTier == null || rebateTier.toLowerCase() === RebateTier.NONE) {
        return '';
      }

      const rebateOption = this.rebateTierInfo
        .find((tier) => {
          return tier.myCircumstance === myCircumstancesValue;
        })
        .rebateTierOptions.find((op) => {
          return op.dhl_code === rebateTier;
        });

      return `${this.formatRebateTierValue(rebateOption.dhl_code)} - ${rebateOption.dhl_name}`;
    },

    getRebateApplicantEnum(personCode) {
      if (personCode === null) return 'partner'; // New partner without personCode

      const foundApplicant = this.rebateApplicants.find((person) => {
        return person.personCode === personCode;
      });

      if (foundApplicant !== undefined && foundApplicant.relationship.toUpperCase() === RelationshipValue.POLICY_HOLDER.toUpperCase()) {
        return 'policyHolder';
      }

      return 'partner';
    },

    getRebateCommencementDate() {
      this.rebateCommencementDate = this.membershipPolicy.rebateCommencementDate;
    },

    generatePayload() {
      const payload = {
        rebateTier: this.formResponse.rebateTier,
        myCircumstances: this.formResponse.myCircumstances,
        rebateApplicant: this.getRebateApplicantEnum(this.getRebateApplicant()),
        isFormPrefilled: this.formPrefilled,
      };

      if (this.isWithdrawRebateTier) {
        payload.op = OperationType.REMOVE;
      } else if (this.isUpdateRebateTier) {
        if (this.noRebateUpdates) {
          payload.op = OperationType.NONE;
          payload.myCircumstances = this.getMyCircumstances;
        } else {
          payload.op = OperationType.REPLACE;
        }
      } else if (this.formResponse.isRebateApplied === false) {
        // Add a rebate, but choose not to apply rebate
        payload.op = OperationType.NONE;
        payload.rebateTier = RebateTier.NONE;
        payload.rebateApplicant = null;
        payload.myCircumstances = this.getMyCircumstances;
      } else if (this.isAddRebate) {
        payload.op = OperationType.ADD;
        payload.rebateApplicant = this.getRebateApplicantEnum(this.formResponse.rebateApplicant);
      } else {
        // if not allowed to add/update/withdraw rebate due to registration status & event type rules
        payload.op = OperationType.NONE;
      }

      if ([OperationType.NONE, OperationType.REMOVE, OperationType.REPLACE].includes(payload.op)) {
        // Will return {} if no available medicare card
        // getMedicareCardDetails second parameter to false so the expiry date will not be formatted to DD / MM / YYYY
        const existingMedicareCard = this.getMedicareCardDetailsOfRebateApplicant();

        // When op is "none", "remove" or "replace", it will take existing medicare card
        payload.medicareCard = existingMedicareCard;
      } else {
        // Format new medicare card expiry date to YYYY-MM-DD if existing
        const newMedicareCard = {
          ...this.formResponse.medicareCard,
          cardNumber: removeWhitespaceFromString(this.formResponse.medicareCard.cardNumber), // Remove whitespace in card number
          cardType: mapMedicareCardCode(this.formResponse.medicareCard.cardType), // Map medicare card to one of these values [G, B, Y]
        };

        if (newMedicareCard.expiryDate) {
          newMedicareCard.expiryDate = formatFormDate(this.formResponse.medicareCard.expiryDate);
        }

        // If op is "add", it will take the new medicare card details in current step
        payload.medicareCard = newMedicareCard;
      }

      return payload;
    },

    getMedicareCardDetailsOfRebateApplicant() {
      const rebateApplicantPersonCode = this.membershipPolicy.rebateApplicant;
      const rebateApplicantMember = this.memberDetails.find((member) => {
        return member.personCode === rebateApplicantPersonCode;
      });

      let medicareCardData = {};

      if (rebateApplicantMember) {
        const medicareCard = rebateApplicantMember?.medicareCard;
        if (medicareCard != null) {
          medicareCardData = {
            id: medicareCard.id != null ? medicareCard.id : '',
            cardType: mapMedicareCardCode(medicareCard.cardType),
            cardNumber: removeWhitespaceFromString(medicareCard.cardNumber),
            firstName: medicareCard.firstName != null ? medicareCard.firstName : '',
            lastName: medicareCard.lastName != null ? medicareCard.lastName : '',
            referenceNumber: medicareCard.referenceNumber != null ? medicareCard.referenceNumber : '',
          };

          if (medicareCard.expiryDate) {
            medicareCardData.expiryDate = medicareCard.expiryDate;
          }
        }
      }
      return medicareCardData;
    },

    prefillData() {
      this.getInitialMyCircumstances();
      this.formPrefilled = this.rebateInformationStep.isFormPrefilled ?? false;
      // If first time come into this page, prefill data that from changeCoverData and CRM
      if (this.formPrefilled === false) {
        // If selected Single composition, use the provided My Circumstances from step 2, otherwise infer 'other'
        const myCircumstances = this.changeCoverData.step2.composition === CoverComposition.SINGLE ? this.changeCoverData.step2.myCircumstances : MyCircumstances.OTHER;

        this.formResponse = {
          rebateApplicant: this.getRebateApplicant(),
          myCircumstances,
          rebateTier: this.changeCoverData.step2.rebateTier,
        };

        // For add rebate form
        if (this.isAddRebate) {
          this.formResponse.isRebateApplied = this.changeCoverData.step2.rebateTier.toUpperCase() !== RebateTier.NONE.toUpperCase();
          this.formResponse.isPolicyCovered = null;
          this.formResponse.isMedicareEligible = null;
        }

        if (this.isWithdrawRebateTier) {
          this.getRebateCommencementDate();
        }
      } else {
        // Prefill data from rebateInformationStep
        this.formResponse = {
          rebateApplicant: this.rebateInformationStep.rebateApplicant,
          myCircumstances: this.rebateInformationStep.myCircumstances,
          rebateTier: this.rebateInformationStep.rebateTier,
        };
        // As rebateApplicant stored in rebateInformationStep is either policyHolder or partner
        this.selectedApplicant = this.rebateInformationStep.rebateApplicant;

        if (this.isAddRebate) {
          if (this.rebateInformationStep.rebateTier && this.rebateInformationStep.rebateTier.toUpperCase() !== RebateTier.NONE.toUpperCase()) {
            this.formResponse.isRebateApplied = true;
            this.formResponse.isPolicyCovered = true;
            this.formResponse.isMedicareEligible = true;
            // Prefill medicare card info
            if (this.rebateInformationStep?.medicareCard) {
              this.formResponse.medicareCard = {
                ...this.rebateInformationStep.medicareCard,
                cardType: mapMedicareCardCode(this.rebateInformationStep.medicareCard.cardType, false),
                expiryDate: reverseFormatFormDate(this.rebateInformationStep.medicareCard.expiryDate),
                cardNumber: formatMedicareCardNumber(this.rebateInformationStep.medicareCard.cardNumber),
              };
            }
          } else {
            // Not applied rebate
            this.formResponse.rebateTier = RebateTier.NONE;
            this.formResponse.isRebateApplied = false;
            this.formResponse.isPolicyCovered = null;
            this.formResponse.isMedicareEligible = null;
          }
        }
      }

      if (this.isAddRebate) {
        // Give a default value for checkbox to avoid unable to reselect issue
        this.formResponse.rebateDeclaration = null;
        this.formResponse.rebateAcceptance = null;
      }

      this.formPrefilled = true;
    },

    reverseFormatFormDate,
    format,
  },
};
</script>

<style lang="scss" scoped>
.radio-group {
  input[type='radio'] {
    & + .income-label {
      display: block;
      font-weight: $NORMAL;
    }
  }
}

.no-margin-bottom {
  margin-bottom: 0;
}

:deep(.disabled-input) {
  border: unset;
}
</style>
